import { createContext, useContext, type ReactNode } from 'react';
import type { AppSettings, FormSchema } from '../types';
import type { Form } from '../models/Form';
import type { FormService } from '../services/formService';
import type { StateService } from '../services/stateService';
import type { IDisplayService } from '../services/displayService';
import type { IValidationService } from '../services/validationService';
import type { DataService } from '../services/dataService';
import type { DataSummarizer } from '../services/dataSummarizer';
import type { ScrollService } from '../services/scrollService';
import type { ModalService } from '../services/modalService';
import type { RecaptchaService } from '../services/recaptchaService';
import type { FieldInfoService } from '../services/fieldInfoService';
import type { NZBNService } from '../services/nzbnService';
import type { HttpService } from '../services/httpService';
import type { GuidService } from '../services/guidService';

export interface FormContextValue {
  settings: AppSettings;
  schema: FormSchema;
  form: Form;
  formService: FormService;
  stateService: StateService;
  displayService: IDisplayService;
  validationService: IValidationService;
  dataService: DataService;
  dataSummarizer: DataSummarizer;
  scrollService: ScrollService;
  modalService: ModalService;
  recaptchaService: RecaptchaService;
  fieldInfoService: FieldInfoService;
  nzbnService: NZBNService;
  httpService: HttpService;
  guidService: GuidService;
  groupSetCounts: Record<string, number>;
  setGroupSetCount: (groupSetId: string, count: number) => void;
  forceUpdate: () => void;
}

const FormContext = createContext<FormContextValue | null>(null);

export function FormProvider({
  value,
  children,
}: {
  value: FormContextValue;
  children: ReactNode;
}) {
  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export function useFormContext(): FormContextValue {
  const context = useContext(FormContext);

  if (!context) {
    throw new Error('useFormContext must be used within FormProvider');
  }

  return context;
}
